import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import {
  MemberDeleteOptions,
  Mutation,
  MutationAddVisitorArgs,
  MutationAddVisitorGroupArgs,
  MutationDeleteVisitorByIdArgs,
  MutationDeleteVisitorGroupByIdArgs,
  MutationSendInvitationForMobileDeviceByIdArgs,
  MutationSetVisitorByIdArgs,
  MutationUnregisterMobileDeviceByIdArgs,
  MutationUpdateVisitorGroupArgs,
  Query,
  QueryGetVisitorByIdArgs,
  QueryGetVisitorGroupsForTenantArgs,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import {
  ADD_VISITOR_GROUP,
  ADD_VISITOR_GROUP_WITH_RETAIL,
  UPDATE_VISITOR_GROUP,
  UPDATE_VISITOR_GROUP_WITH_RETAIL
} from 'graphql/mutations';
import { GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS } from 'graphql/queries';
import {
  ADD_VISITOR_WITH_ROLE,
  DELETE_VISITOR_BY_ID,
  DELETE_VISITOR_GROUP_BY_ID,
  SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID,
  SET_VISITOR_BY_ID_WITH_ROLE,
  UNREGISTER_MOBILE_DEVICE_BY_ID
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS,
  GET_ALL_VISITORS_VISITOR_GROUPS,
  GET_VISITOR_DETAILS,
  GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE
} from 'graphql/v2/queries';
import { updateVisitorGroupMembershipWithAdminGroup } from 'hooks/allVisitors/drawers/utils';
import { useEffect } from 'react';
import {
  addExternalUsersGroupsAction,
  addExternalUsersMemberAction,
  deleteVisitorByIdAction,
  deleteVisitorGroupByIdAction,
  disconnectMobileDeviceAction,
  setExternalUsersGroupsAction,
  setExternalUsersMemberAction,
  updateExternalUsersGroupsAction
} from 'state/actions/externalUsersGroups';
import { externalUsersGroupsVar } from 'state/vars';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { TranslatedHookProps } from '../shared/types';

const useExternalUsers = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const externalUsersGroups = useReactiveVar(externalUsersGroupsVar);
  const [getExternalUsersMembersWithRole, { loading: loadingWithRole }] = useLazyQuery<
    Pick<Query, 'getVisitorGroupsForTenant'>,
    QueryGetVisitorGroupsForTenantArgs
  >(GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE, {
    onCompleted: (data) => {
      setExternalUsersGroupsAction(data.getVisitorGroupsForTenant);
    },
    onError: () => {
      handleFetchError(translation.external_groups_error_fetching);
    }
  });

  useEffect(() => {
    getExternalUsersMembersWithRole({
      variables: {
        filter: { type: VisitorGroupType.CoContractors }
      }
    });
  }, [getExternalUsersMembersWithRole]);

  const [fetchVisitorDetails, { loading: fetchVisitorDetailsLoading, error: fetchVisitorDetailsError }] = useLazyQuery<
    Pick<Query, 'getVisitorById'>,
    QueryGetVisitorByIdArgs
  >(GET_VISITOR_DETAILS);

  const [
    fetchAdministratorsVisitorGroups,
    { loading: administratorsVisitorGroupsLoading, error: administratorsVisitorGroupsError }
  ] = useLazyQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS, {
    variables: {
      filter: { type: VisitorGroupType.Administrators }
    }
  });

  useEffect(() => {
    if (administratorsVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(administratorsVisitorGroupsError);
    }
  }, [administratorsVisitorGroupsError]);

  const [
    fetchInstallersVisitorGroups,
    { loading: installersVisitorGroupsLoading, error: installersVisitorGroupsError }
  ] = useLazyQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_ALL_VISITORS_VISITOR_GROUPS, {
    variables: {
      filter: { type: VisitorGroupType.Installers }
    }
  });

  useEffect(() => {
    if (installersVisitorGroupsError) {
      handleFetchError(translation.user_groups_error_fetching);
      console.error(installersVisitorGroupsError);
    }
  }, [installersVisitorGroupsError]);

  const [addVisitorGroupMutation, { loading: addVisitorGroupMutationLoading }] = useMutation<
    Pick<Mutation, 'addVisitorGroup'>,
    MutationAddVisitorGroupArgs
  >(ADD_VISITOR_GROUP, {
    refetchQueries: [
      { query: GET_ALL_VISITORS_VISITOR_GROUPS, variables: { filter: { type: VisitorGroupType.Administrators } } },
      { query: GET_ALL_VISITORS_VISITOR_GROUPS, variables: { filter: { type: VisitorGroupType.Installers } } }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      addExternalUsersGroupsAction(data.addVisitorGroup);
    },
    onError: () => {
      handleFetchError(translation.external_groups_error_adding);
    }
  });

  const [
    addVisitorGroupWithMaxNumberOfMembersMutation,
    { loading: addVisitorGroupWithMaxNumberOfMembersMutationLoading }
  ] = useMutation<Pick<Mutation, 'addVisitorGroup'>, MutationAddVisitorGroupArgs>(ADD_VISITOR_GROUP_WITH_RETAIL, {
    refetchQueries: [
      { query: GET_ALL_VISITORS_VISITOR_GROUPS, variables: { filter: { type: VisitorGroupType.Administrators } } },
      { query: GET_ALL_VISITORS_VISITOR_GROUPS, variables: { filter: { type: VisitorGroupType.Installers } } }
    ],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      addExternalUsersGroupsAction(data.addVisitorGroup);
    },
    onError: () => {
      handleFetchError(translation.external_groups_error_adding);
    }
  });

  const [updateVisitorGroupMutation, { loading: updateVisitorGroupMutationLoading }] = useMutation<
    Pick<Mutation, 'updateVisitorGroup'>,
    MutationUpdateVisitorGroupArgs
  >(UPDATE_VISITOR_GROUP, {
    onCompleted: (data) => {
      updateExternalUsersGroupsAction(data.updateVisitorGroup);
    },
    onError: () => {
      handleFetchError(translation.external_groups_error_updating);
    }
  });

  const [
    updateVisitorGroupWithMaxNumberOfMembersMutation,
    { loading: updateVisitorGroupWithMaxNumberOfMembersMutationLoading }
  ] = useMutation<Pick<Mutation, 'updateVisitorGroup'>, MutationUpdateVisitorGroupArgs>(
    UPDATE_VISITOR_GROUP_WITH_RETAIL,
    {
      onCompleted: (data) => {
        updateExternalUsersGroupsAction(data.updateVisitorGroup);
      },
      onError: () => {
        handleFetchError(translation.external_groups_error_updating);
      }
    }
  );

  const [
    setVisitorByIdWithRoleMutation,
    { error: setVisitorByIdWithRoleMutationError, loading: updateVisitorMemberByIdWithRoleLoading }
  ] = useMutation<Pick<Mutation, 'setVisitorById'>, MutationSetVisitorByIdArgs>(SET_VISITOR_BY_ID_WITH_ROLE, {
    onCompleted: (data) => data,
    onError: (error) => {
      console.error(error.message);
      if (error.message.includes('Installers cannot have expiration date'))
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_updating);
    }
  });

  const [addVisitorMemberWithRoleMutation, { loading: addVisitorMemberMutationWithRoleLoading }] = useMutation<
    Pick<Mutation, 'addVisitor'>,
    MutationAddVisitorArgs
  >(ADD_VISITOR_WITH_ROLE, {
    onCompleted: (data) => data,
    onError: (error) => {
      console.error(error);
      if (error.message === 'Visitor already exists') handleFetchError(translation.user_email_already_exists);
      else if (error.message === 'Installers cannot have expiration date.')
        handleFetchError(translation.expiration_date_error_installer);
      else handleFetchError(translation.external_groups_user_error_adding);
    }
  });

  const [deleteVisitorByIdMutation, { loading: deleteVisitorByIdMutationLoading }] = useMutation<
    Pick<Mutation, 'deleteVisitorById'>,
    MutationDeleteVisitorByIdArgs
  >(DELETE_VISITOR_BY_ID, {
    onCompleted: (data) => {
      deleteVisitorByIdAction(data.deleteVisitorById || null);
    },
    onError: () => {
      handleFetchError(translation.external_groups_user_error_deleting);
    }
  });
  const [
    deleteVisitorGroupByIdMutation,
    { error: deleteVisitorGroupByIdMutationError, loading: deleteVisitorGroupByIdMutationLoading }
  ] = useMutation<Pick<Mutation, 'deleteVisitorGroupById'>, MutationDeleteVisitorGroupByIdArgs>(
    DELETE_VISITOR_GROUP_BY_ID,
    {
      refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS }],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        deleteVisitorGroupByIdAction(data.deleteVisitorGroupById || null);
      },
      onError: (error) => {
        handleFetchError(error.message);
      }
    }
  );

  const addVisitorGroup = async (name: string) => {
    const { data } = await addVisitorGroupMutation({
      variables: { name, type: VisitorGroupType.CoContractors },
      update(cache, { data: addedGroup }) {
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: [...externalUsersGroups, addedGroup?.addVisitorGroup]
          }
        });
      }
    });
    return data;
  };

  const addVisitorGroupWithMaxNumberOfMembers = async (name: string, maxNumberOfMembers: number | null) => {
    const { data } = await addVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, maxNumberOfMembers, type: VisitorGroupType.CoContractors },
      update(cache, { data: addedGroup }) {
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: [...externalUsersGroups, addedGroup?.addVisitorGroup]
          }
        });
      }
    });
    return data;
  };

  const [sendInvitationForMobileDeviceByIdMutation, { loading: sendInvitationForMobileDeviceByIdMutationLoading }] =
    useMutation<Pick<Mutation, 'sendInvitationForMobileDeviceById'>, MutationSendInvitationForMobileDeviceByIdArgs>(
      SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID,
      {
        onError: (error) => {
          if (
            error.message.includes('Too many requests') ||
            error.message === 'MobileDevice does not have registration token'
          )
            handleFetchError(translation.unable_to_send_invite_please_wait_10_minutes_before_trying_again);
          else handleFetchError(translation.error_while_resending_invitation);
        }
      }
    );

  const updateVisitorGroup = async (name: string, visitorGroupId: string) => {
    const { data } = await updateVisitorGroupMutation({
      variables: { name, visitorGroupId },
      update(cache, { data: updatedGroup }) {
        const updatedGroups = externalUsersGroups.map((group) => {
          if (group.id === updatedGroup?.updateVisitorGroup.id) {
            return updatedGroup?.updateVisitorGroup;
          }
          return group;
        });
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  const updateVisitorGroupWithMaxNumberOfMembers = async (
    name: string,
    visitorGroupId: string,
    maxNumberOfMembers: number | null
  ) => {
    const { data } = await updateVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, visitorGroupId, maxNumberOfMembers },
      update(cache, { data: updatedGroup }) {
        const updatedGroups = externalUsersGroups.map((group) => {
          if (group.id === updatedGroup?.updateVisitorGroup.id) {
            return updatedGroup?.updateVisitorGroup;
          }
          return group;
        });
        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  type AddOrEditVisitorArgs = {
    name: string;
    visitorGroupMembershipInput: VisitorGroupMembershipInput[];
    shouldBeAdmin: boolean;
    email?: string;
    id?: string;
    expirationDate?: dayjs.Dayjs;
  };

  const addOrEditVisitor = async ({
    name,
    shouldBeAdmin,
    email,
    id,
    expirationDate,
    visitorGroupMembershipInput
  }: AddOrEditVisitorArgs) => {
    const [adminGroupData, installerGroupData, userDetails] = await Promise.all([
      fetchAdministratorsVisitorGroups(),
      fetchInstallersVisitorGroups(),
      id ? fetchVisitorDetails({ variables: { id } }) : undefined
    ]);
    let result;
    if (
      adminGroupData.data?.getVisitorGroupsForTenant === undefined ||
      installerGroupData.data?.getVisitorGroupsForTenant === undefined ||
      (id && userDetails?.data?.getVisitorById === undefined)
    ) {
      handleFetchError(translation.external_groups_user_error_updating);
    } else {
      const isVisitorInstallerAlready = installerGroupData.data.getVisitorGroupsForTenant.some((group) =>
        group.members.some((member) => member.email === email)
      );
      const isUserExistAlready = userDetails?.data?.getVisitorById;
      const expirationDateIso = expirationDate ? dayjs(expirationDate).toISOString() : undefined;
      const membershipWithExpirationDate = expirationDateIso
        ? (visitorGroupMembershipInput || []).map((membership) => ({
            ...membership,
            expirationDate: expirationDateIso
          }))
        : visitorGroupMembershipInput || [];
      let updatedVisitorGroupMembership: VisitorGroupMembershipInput[] = updateVisitorGroupMembershipWithAdminGroup({
        visitorGroupMembership: membershipWithExpirationDate,
        options: {
          shouldBeAdmin,
          adminGroup: adminGroupData.data.getVisitorGroupsForTenant.find(
            (group) => group.type === VisitorGroupType.Administrators
          ),
          shouldBeInstaller: isVisitorInstallerAlready,
          installerGroup: installerGroupData.data.getVisitorGroupsForTenant.find(
            (group) => group.type === VisitorGroupType.Installers
          )!
        }
      });
      if (!isUserExistAlready && !isVisitorInstallerAlready) {
        const { data } = await addVisitorMemberWithRoleMutation({
          variables: {
            name,
            email,
            visitorGroupMembership: updatedVisitorGroupMembership
          },
          update(cache, { data: addedMember }) {
            const updatedGroups = externalUsersGroups.map((group) => {
              if (group.id === visitorGroupMembershipInput[0].visitorGroupId && addedMember) {
                return { ...group, members: [...group.members, { ...addedMember.addVisitor, isAdmin: shouldBeAdmin }] };
              }
              return group;
            });
            cache.writeQuery({
              query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
              variables: {
                filter: { type: VisitorGroupType.CoContractors }
              },
              data: {
                getVisitorGroupsForTenant: updatedGroups
              }
            });
          }
        });
        if (data) {
          addExternalUsersMemberAction(
            { ...data.addVisitor, isAdmin: shouldBeAdmin },
            visitorGroupMembershipInput[0].visitorGroupId!
          );
        }
        return data?.addVisitor;
      } else if (id || isVisitorInstallerAlready) {
        const installerGroup = installerGroupData.data.getVisitorGroupsForTenant.find((group) => group);
        const installerId = installerGroup?.members.find((member) => member.email === email)?.id;
        const { data } = await setVisitorByIdWithRoleMutation({
          variables: {
            visitorId: id || installerId || '',
            name,
            visitorGroupMembership: updatedVisitorGroupMembership
          },
          update(cache, { data: updatedMember }) {
            const updatedGroups = externalUsersGroups.map((group) => {
              if (
                updatedMember &&
                updatedMember.setVisitorById.visitorGroupMembership.some(
                  (updatedMemberGroup) => updatedMemberGroup.visitorGroup.id === group.id
                )
              )
                if (group.members.some((member) => member.id === updatedMember?.setVisitorById.id))
                  return {
                    ...group,
                    members: group.members.map((member) =>
                      member.id === updatedMember?.setVisitorById.id
                        ? { ...updatedMember?.setVisitorById, isAdmin: updatedMember.setVisitorById.isAdmin }
                        : member
                    )
                  };
                else
                  return {
                    ...group,
                    members: [...group.members, updatedMember.setVisitorById]
                  };
              return {
                ...group,
                members: group.members.filter((member) => member.id !== updatedMember?.setVisitorById.id)
              };
            });

            cache.writeQuery({
              query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE,
              variables: {
                filter: { type: VisitorGroupType.CoContractors }
              },
              data: {
                getVisitorGroupsForTenant: updatedGroups
              }
            });
          }
        });
        if (data) {
          setExternalUsersMemberAction(
            { ...data.setVisitorById, isAdmin: shouldBeAdmin },
            visitorGroupMembershipInput.map((membership) => membership.visitorGroupId)
          );
        }
        return data?.setVisitorById;
      }
    }
    return result;
  };

  const deleteVisitorById = async (visitorId: string) => {
    const { data } = await deleteVisitorByIdMutation({
      variables: { visitorId },
      update(cache, { data: deletedMemberData }) {
        const groupWithDeletedMember = externalUsersGroups.filter((group) =>
          group.members.some((member) => member.id === visitorId)
        )[0];

        const updatedGroups = externalUsersGroups.map((group) => {
          if (group.id === groupWithDeletedMember.id)
            return {
              ...group,
              members: group.members.filter((member) => member.id !== deletedMemberData?.deleteVisitorById)
            };
          return group;
        });

        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  const deleteVisitorGroupById = async (visitorGroupId: string) => {
    const { data } = await deleteVisitorGroupByIdMutation({
      variables: { visitorGroupId, options: MemberDeleteOptions.AtlasManagedVisitors },
      update(cache, { data: removedVisitorGroupById }) {
        const updatedGroups = externalUsersGroups.filter(
          (group) => group.id !== removedVisitorGroupById?.deleteVisitorGroupById
        );

        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    });
    return data;
  };

  const sendInvitationForMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await sendInvitationForMobileDeviceByIdMutation({
      variables: { visitorId, mobileDeviceId }
    });
    return data;
  };

  const [unregisterMobileDeviceByIdMutation, { loading: unregisterMobileDeviceByIdMutationLoading }] = useMutation<
    Pick<Mutation, 'unregisterMobileDeviceById'>,
    MutationUnregisterMobileDeviceByIdArgs
  >(UNREGISTER_MOBILE_DEVICE_BY_ID, {
    onError: () => handleFetchError(translation.error_while_unregister_mobile_device)
  });

  const unregisterMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await unregisterMobileDeviceByIdMutation({
      variables: {
        visitorId,
        mobileDeviceId
      },
      update(cache, { data }) {
        const groupWithDeletedMember = externalUsersGroups.find((group) =>
          group.members.some((member) => member.id === visitorId)
        );

        const memberToDelete = groupWithDeletedMember?.members.find((member) => member.id === visitorId);
        const updatedMember = {
          ...memberToDelete,
          mobileDevices: memberToDelete?.mobileDevices.map((mobileDevice) =>
            mobileDevice.id === mobileDeviceId
              ? { ...mobileDevice, isRegistered: false, registrationDate: null, registrationToken: null }
              : mobileDevice
          )
        };
        const updatedGroups = externalUsersGroups.map((group) => {
          if (group.id === groupWithDeletedMember?.id)
            return {
              ...group,
              members: group.members.map((member) => (member.id === visitorId ? updatedMember : member))
            };
          return group;
        });

        cache.writeQuery({
          query: GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS,
          variables: {
            filter: { type: VisitorGroupType.CoContractors }
          },
          data: {
            getVisitorGroupsForTenant: updatedGroups
          }
        });
      }
    }).finally(() => disconnectMobileDeviceAction(visitorId, mobileDeviceId));

    return data;
  };

  return {
    externalUsersGroups,
    addVisitorGroup,
    addVisitorGroupMutationLoading,
    addVisitorGroupWithMaxNumberOfMembers,
    addVisitorGroupWithMaxNumberOfMembersMutationLoading,
    updateVisitorGroup,
    updateVisitorGroupMutationLoading,
    updateVisitorGroupWithMaxNumberOfMembers,
    updateVisitorGroupWithMaxNumberOfMembersMutationLoading,
    addOrEditVisitor,
    updateVisitorMemberByIdWithRoleLoading,
    addVisitorMemberMutationWithRoleLoading,
    deleteVisitorById,
    deleteVisitorByIdMutationLoading,
    deleteVisitorGroupByIdMutationError,
    deleteVisitorGroupByIdMutationLoading,
    deleteVisitorGroupById,
    setVisitorByIdWithRoleMutationError,
    sendInvitationForMobileDeviceById,
    sendInvitationForMobileDeviceByIdMutationLoading,
    loadingWithRole,
    unregisterMobileDeviceById,
    unregisterMobileDeviceByIdMutationLoading
  };
};

export default useExternalUsers;
